import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { KpiCard } from "@/components/KpiCard";
import { Badge } from "@/components/Badge";
import { TipoIcon } from "@/components/TipoIcon";
import { STAGE_COLORS, TIPO_BADGE } from "@/lib/constants";
import { MessageCircle } from "lucide-react";
import { getPratiche } from "@/api/pratiche";
import { getBollette } from "@/api/bollette";
import { formatDateTime } from "@/lib/dates";
import type { Pratica, BollettaApi } from "@/types";

interface CardState<T> {
  loading: boolean;
  data: T | null;
  error: boolean;
}

function initCard<T>(): CardState<T> {
  return { loading: true, data: null, error: false };
}

export function DashboardCliente() {
  const navigate = useNavigate();
  const [praticheState, setPraticheState] = useState<CardState<Pratica[]>>(initCard());
  const [bolletteState, setBolletteState] = useState<CardState<{ total: number; items: BollettaApi[] }>>(initCard());

  useEffect(() => {
    Promise.allSettled([
      getPratiche(),
      getBollette(1, 0),
    ]).then(([praticheRes, bolletteRes]) => {
      if (praticheRes.status === "fulfilled") {
        setPraticheState({ loading: false, data: praticheRes.value, error: false });
      } else {
        setPraticheState({ loading: false, data: null, error: true });
      }

      if (bolletteRes.status === "fulfilled") {
        setBolletteState({ loading: false, data: bolletteRes.value, error: false });
      } else {
        setBolletteState({ loading: false, data: null, error: true });
      }
    });
  }, []);

  const pratiche = praticheState.data ?? [];
  const praticheInLavorazione = pratiche.filter((p) => p.stage === "In lavorazione").length;
  const praticheTotal = pratiche.length;

  const bolletteTotal = bolletteState.data?.total ?? 0;
  const ultimaBollettaItem = bolletteState.data?.items?.[0] ?? null;

  const kpiUltimaBolletta = bolletteState.error
    ? "Errore"
    : bolletteState.loading
    ? "…"
    : ultimaBollettaItem
    ? `€${ultimaBollettaItem.importoMensile.toFixed(2)}`
    : "—";

  const kpiUltimaBollettaSubtitle = ultimaBollettaItem
    ? `${ultimaBollettaItem.fornitoreAttuale} · ${ultimaBollettaItem.tipoFornitura}`
    : undefined;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-4 gap-4">
        <KpiCard
          label="Pratiche totali"
          value={praticheState.error ? "Errore" : praticheState.loading ? "…" : praticheTotal}
          variant="hero"
        />
        <KpiCard
          label="In lavorazione"
          value={praticheState.error ? "Errore" : praticheState.loading ? "…" : praticheInLavorazione}
        />
        <KpiCard
          label="Ultima bolletta"
          value={kpiUltimaBolletta}
          subtitle={kpiUltimaBollettaSubtitle}
        />
        <KpiCard
          label="Bollette totali"
          value={bolletteState.error ? "Errore" : bolletteState.loading ? "…" : bolletteTotal}
        />
      </div>

      <div className="bg-gradient-to-r from-brand to-brand-light text-white p-5 rounded-xl flex items-center justify-between">
        <div>
          <div className="font-semibold">Vuoi risparmiare sulla bolletta?</div>
          <div className="text-sm opacity-90">Analizza una nuova bolletta con il nostro assistente AI</div>
        </div>
        <button
          onClick={() => navigate("/chat")}
          className="bg-white text-brand px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 hover:opacity-90 transition-colors"
        >
          <MessageCircle size={16} /> Nuova analisi
        </button>
      </div>

      <div className="bg-card rounded-lg border border-border p-4">
        <h2 className="font-semibold text-text mb-3">Pratiche</h2>
        {praticheState.loading && (
          <p className="text-sm text-muted-foreground">Caricamento...</p>
        )}
        {praticheState.error && (
          <p className="text-sm text-red-600">Impossibile caricare le pratiche.</p>
        )}
        {!praticheState.loading && !praticheState.error && pratiche.length === 0 && (
          <p className="text-sm text-muted-foreground">Nessuna pratica ancora.</p>
        )}
        {pratiche.slice(0, 5).map((p) => {
          const stageStyle = STAGE_COLORS[p.stage] ?? { bg: "bg-gray-100", text: "text-gray-800" };
          const tipoStyle = TIPO_BADGE[p.tipo] ?? { label: p.tipo, bg: "bg-gray-100", text: "text-gray-800" };
          return (
            <div
              key={p.id}
              className="flex items-center justify-between py-2 border-b border-border last:border-0 cursor-pointer hover:bg-surface rounded px-2 -mx-2"
              onClick={() => navigate(`/pratiche/${p.id}`)}
            >
              <div>
                <span className="font-medium text-text">{p.nome}</span>
                <span className="text-sm text-muted-foreground ml-2 inline-flex items-center gap-1">· <TipoIcon tipo={p.tipo} />{tipoStyle.label}</span>
              </div>
              <div className="flex items-center gap-3">
                <Badge bg={stageStyle.bg} text={stageStyle.text}>{p.stage}</Badge>
                <span className="text-xs text-muted-foreground">{formatDateTime(p.dataCreazione)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
